import type {
  Ajustes, AjustesPlataforma, Borrador, Capacidad, Caso, Config, Corrida,
  CredencialInfo, DescubrimientoMCP, FilaTenant, Inteligencia, ResultadoPrueba,
  Implementacion, Precio, ProveedorModelo, ResumenCorrida, TrazaReal, TurnoConversacion, Usuario, Veredicto, VersionGuardada, Vigencia, VistaAlertas, VistaErrores,
} from '@/tipos'

// Cliente HTTP del API de administracion.
//
// Sin axios: fetch alcanza para nueve endpoints, y una dependencia menos es una
// dependencia menos que parchear. Lo unico que hace falta encapsular es el token
// y la forma de los errores.

export class ErrorAPI extends Error {
  constructor(public codigo: number, mensaje: string) {
    super(mensaje)
  }
  get esSesionCaida() { return this.codigo === 401 }
  get esPermiso()     { return this.codigo === 403 }
  get esConflicto()   { return this.codigo === 409 }
}

const CLAVE_TOKEN = 'botia.token'

export const token = {
  leer:     () => localStorage.getItem(CLAVE_TOKEN) ?? '',
  guardar:  (t: string) => localStorage.setItem(CLAVE_TOKEN, t),
  olvidar:  () => localStorage.removeItem(CLAVE_TOKEN),
}

/** `crudo` manda el cuerpo tal cual, sin pasarlo por JSON. Es para el guion
 *  pegado: convertirlo a JSON aquí obligaría a interpretar el YAML en el
 *  navegador, y quien tiene que decidir si es válido es el servidor. */
async function pedir<T>(metodo: string, ruta: string, cuerpo?: unknown, crudo = false): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  const t = token.leer()
  if (t) cabeceras.Authorization = `Bearer ${t}`
  if (cuerpo !== undefined) cabeceras['Content-Type'] = crudo ? 'text/yaml' : 'application/json'

  let r: Response
  try {
    r = await fetch(ruta, {
      method: metodo,
      headers: cabeceras,
      body: cuerpo === undefined ? undefined : crudo ? String(cuerpo) : JSON.stringify(cuerpo),
    })
  } catch {
    // Distinguir "no hay red" de "el servidor dijo que no" importa: son dos
    // cosas distintas que hacer.
    throw new ErrorAPI(0, 'No se pudo contactar el servidor. ¿Está corriendo la consola?')
  }

  if (r.status === 204) return undefined as T

  const texto = await r.text()
  let datos: any = null
  try { datos = texto ? JSON.parse(texto) : null } catch { /* respuesta no JSON */ }

  if (!r.ok) {
    throw new ErrorAPI(r.status, datos?.error ?? `El servidor respondió ${r.status}`)
  }
  return datos as T
}

export const api = {
  // Entrar: los dos únicos que no llevan token, porque son por donde se entra.
  //
  // pedirCodigo responde igual exista o no la cuenta. Ese mensaje se muestra
  // tal cual y no se interpreta: distinguir aquí "no existe" de "ya va" haría
  // del formulario un directorio del personal.
  pedirCodigo: (correo: string) =>
    pedir<{ enviado: boolean; mensaje: string }>('POST', '/api/acceso/codigo', { correo }),

  entrarConCodigo: (correo: string, codigo: string) =>
    pedir<{ token: string }>('POST', '/api/acceso/entrar', { correo, codigo }),

  // Salir revoca la sesión en el SERVIDOR. Borrarla solo del navegador deja el
  // token vivo para quien lo tenga copiado.
  salir: () => pedir<{ cerrada: boolean }>('POST', '/api/acceso/salir', {}),

  yo: () => pedir<Usuario>('GET', '/api/yo'),

  // Quien puede entrar y desde donde. No da ni quita permisos: eso sigue en la
  // pantalla de seguridad del sistema viejo.
  usuariosConsola: () =>
    pedir<{ usuarios: Array<{
      id: number; username: string; nombre: string; email: string; celular: string
      permisos: string[]; sesiones: number; ultimo_acceso: string; email_duplicado: boolean
    }> }>('GET', '/api/acceso/usuarios'),

  // Exige el permiso de publicar: el correo es por donde llega el codigo, asi
  // que cambiarselo a alguien es quedarse con su cuenta.
  guardarContacto: (id: number, email: string, celular: string) =>
    pedir<{ guardado: boolean; cambio_correo: boolean }>(
      'PUT', `/api/acceso/usuarios/${id}/contacto`, { email, celular }),

  sesionesConsola: () =>
    pedir<{ sesiones: Array<{
      id: string; usuario: string; nombre: string; creada: string
      ultimo_uso: string; expira: string; ip: string; agente: string
    }> }>('GET', '/api/acceso/sesiones'),

  revocarSesionConsola: (id: string) =>
    pedir<{ revocada: boolean }>('DELETE', `/api/acceso/sesiones/${encodeURIComponent(id)}`),

  /** Baja el guion como archivo. Sin versión, el PUBLICADO — que es lo que la
   *  gente quiere decir con "el guion actual": el que atiende ahora mismo.
   *
   *  Va por fetch y no por un <a href>: la ruta exige el token en la cabecera, y
   *  un enlace normal no lo lleva. */
  descargarYAML: async (id: string, version?: number) => {
    const q = version ? `?version=${version}` : ''
    const r = await fetch(`/api/tenants/${encodeURIComponent(id)}/yaml${q}`, {
      headers: { Authorization: `Bearer ${token.leer()}` },
    })
    if (!r.ok) {
      let msg = `El servidor respondió ${r.status}`
      try { msg = (JSON.parse(await r.text()))?.error ?? msg } catch { /* no era JSON */ }
      throw new ErrorAPI(r.status, msg)
    }
    // El nombre lo pone el servidor en Content-Disposition: sabe de qué tenant y
    // de qué versión es, y aquí solo tendríamos el id.
    const cd = r.headers.get('Content-Disposition') ?? ''
    const nombre = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? `${id}.yaml`
    return { blob: await r.blob(), nombre }
  },

  /** Clona el guion de otro tenant AL BORRADOR de este. Nunca publica: un guion
   *  clonado apunta al sistema del cliente de origen y le habla a la gente con
   *  el nombre de otra empresa. */
  clonarGuion: (destino: string, origen: string, version = 0) =>
    pedir<{
      clonado: boolean; origen: string; version_origen: number; nombre_origen: string
      base_version: number
      avisos: Array<{ campo: string; valor: string; detalle: string }>
      problemas: string[]
    }>('POST', `/api/tenants/${encodeURIComponent(destino)}/clonar`, { origen, version }),

  /** Quién espera a un asesor y desde cuándo. Sin esto, encender la caducidad
   *  de esperas es operar a ciegas: el único rastro está en el log del gateway. */
  cola: (id: string) =>
    pedir<{
      esperando: Array<{ motivo: string; cuantas: number; mas_vieja_min: number }>
      caducadas_24h: number
    }>('GET', `/api/tenants/${encodeURIComponent(id)}/cola`),

  catalogo: () => pedir<{ capacidades: Capacidad[] }>('GET', '/api/catalogo'),

  tenants: () => pedir<{ tenants: FilaTenant[] }>('GET', '/api/tenants'),

  vigente: (id: string) => pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}`),

  borrador: (id: string) => pedir<Borrador>('GET', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  guardarBorrador: (id: string, config: Config, base_version: number, nota = '') =>
    pedir<{ guardado: boolean; problemas: string[] }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/borrador`, { config, base_version, nota }),

  /** Guarda como borrador un guion pegado tal cual. Es la puerta para las
   *  partes del config que no tienen formulario propio —el mapeo del adapter,
   *  por ejemplo—, que si no obligan a salir de la consola. */
  pegarYAML: (id: string, yaml: string) =>
    pedir<{ guardado: boolean; aviso?: string; problemas: string[] }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/borrador/yaml`, yaml, true),

  descartarBorrador: (id: string) =>
    pedir<{ descartado: boolean }>('DELETE', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  validar: (id: string, config: Config) =>
    pedir<{ problemas: string[] }>('POST', `/api/tenants/${encodeURIComponent(id)}/validar`, { config }),

  probar: (id: string, config: Config, mensajes: string[], escrituras = false) =>
    pedir<ResultadoPrueba>('POST', `/api/tenants/${encodeURIComponent(id)}/probar`,
      { config, mensajes, escrituras }),

  versiones: (id: string) =>
    pedir<{ versiones: VersionGuardada[]; historial: Vigencia[] }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/versiones`),

  version: (id: string, v: number) =>
    pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}/versiones/${v}`),

  crearVersion: (id: string, nota: string) =>
    pedir<{ version: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/versiones`, { nota }),

  publicar: (id: string, version: number, motivo: string, forzar = false) =>
    pedir<{ publicada: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/publicar`,
      { version, motivo, forzar }),

  despublicar: (id: string, motivo: string) =>
    pedir<{ despublicado: boolean }>('POST', `/api/tenants/${encodeURIComponent(id)}/despublicar`, { motivo }),

  secretos: (id: string) =>
    pedir<{ secretos: Array<{ Nombre: string; Cifrado: boolean; Autor: string; Nota: string; Actualizado: string }> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/secretos`),

  guardarSecreto: (id: string, nombre: string, valor: string, nota = '') =>
    pedir<{ guardado: boolean; caracteres: number; cifrado: boolean; recortado: number }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/secretos/${encodeURIComponent(nombre)}`, { valor, nota }),

  // desde/hasta en YYYY-MM-DD, estado en {con_asesor, cerrada, en_curso, en_limbo}.
  // Vacíos traen todo, que es lo que ve quien entra sin filtrar nada.
  sesiones: (id: string, desde = '', hasta = '', estado = '') => {
    const q = new URLSearchParams()
    if (desde) q.set('desde', desde)
    if (hasta) q.set('hasta', hasta)
    if (estado) q.set('estado', estado)
    const cola = q.toString() ? `?${q}` : ''
    return pedir<{
      sesiones: Array<{
        conversacion: string; paso: string; turnos: number; actualizada: string
        estado: string; inactiva_min: number
      }>
      resumen: Record<string, number>
    }>('GET', `/api/tenants/${encodeURIComponent(id)}/sesiones${cola}`)
  },

  reiniciarSesion: (id: string, conversacion: string) =>
    pedir<{ reiniciada: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/sesiones/${encodeURIComponent(conversacion)}`),

  /** El ida y vuelta con una persona, ordenado en el tiempo. La cola de salida
   *  es media conversación: se ve lo que contestó el bot y hay que adivinar a
   *  qué. */
  conversacion: (id: string, con: string, limite = 100) => {
    const q = new URLSearchParams({ con, limite: String(limite) })
    return pedir<{ turnos: TurnoConversacion[]; trazas?: TrazaReal[]; conversacion_id?: string }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/conversacion?${q}`)
  },

  /** Qué está fallando en este cliente, ahora.
   *
   *  El tablero de Inteligencia dice «errores: 340» y la línea de tiempo de una
   *  conversación muestra la traza entera pero exige saber el teléfono de
   *  antemano. Entre las dos faltaba esto. */
  errores: (id: string, opciones: { horas?: number; clase?: string; capacidad?: string } = {}) => {
    const q = new URLSearchParams()
    if (opciones.horas) q.set('horas', String(opciones.horas))
    if (opciones.clase) q.set('clase', opciones.clase)
    if (opciones.capacidad) q.set('capacidad', opciones.capacidad)
    const cola = q.toString() ? `?${q}` : ''
    return pedir<VistaErrores>('GET', `/api/tenants/${encodeURIComponent(id)}/errores${cola}`)
  },

  salidas: (id: string, para = '', limite = 100) => {
    const q = new URLSearchParams({ limite: String(limite) })
    if (para) q.set('para', para)
    return pedir<{ salidas: Array<Record<string, unknown>> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/salidas?${q}`)
  },

  // -------------------------------------------------------------------------
  // Evaluaciones
  // -------------------------------------------------------------------------

  casos: (id: string) =>
    pedir<{ casos: Caso[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/casos`),

  guardarCaso: (id: string, caso: Partial<Caso>) =>
    pedir<{ id: string }>('POST', `/api/tenants/${encodeURIComponent(id)}/casos`, caso),

  borrarCaso: (id: string, caso: string) =>
    pedir<{ borrado: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/casos/${encodeURIComponent(caso)}`),

  // Propone un caso a partir de una conversación real. No lo guarda: quien la
  // convierte sabe por qué la está guardando, y esa intención es justo lo que
  // hay que escribir en los invariantes.
  casoDesdeConversacion: (id: string, conversacion: string, horas = 24) =>
    pedir<{ caso: Caso; aviso: string }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/casos/desde-conversacion`,
      { conversacion, horas }),

  evaluar: (id: string, config?: Config, casos: string[] = [], guardar = false) =>
    pedir<{ problemas: string[]; corrida: Corrida; avisos?: string[] }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/evaluar`, { config, casos, guardar }),

  corridas: (id: string, limite = 30) =>
    pedir<{ corridas: ResumenCorrida[] }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas?limite=${limite}`),

  corrida: (id: string, corrida: string) =>
    pedir<Corrida>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas/${encodeURIComponent(corrida)}`),

  // -------------------------------------------------------------------------
  // Inteligencia
  // -------------------------------------------------------------------------
  // Estos ajustes NO se versionan: aplican al guardar. Es deliberado — apagar
  // la IA de un cliente a las once de la noche no puede exigir publicar.

  inteligencia: (id: string) =>
    pedir<Inteligencia>('GET', `/api/tenants/${encodeURIComponent(id)}/inteligencia`),

  guardarInteligencia: (id: string, ajustes: Ajustes) =>
    pedir<Inteligencia>('PUT', `/api/tenants/${encodeURIComponent(id)}/inteligencia`, ajustes),

  // Los globales sí: mueven a los treinta clientes a la vez.
  plataforma: () => pedir<AjustesPlataforma>('GET', '/api/plataforma/ajustes'),

  guardarPlataforma: (ajustes: Ajustes, motivo: string, publicar = false) =>
    pedir<{ version: number; publicada: boolean }>(
      'PUT', '/api/plataforma/ajustes', { ajustes, motivo, publicar }),

  publicarPlataforma: (version: number, motivo: string) =>
    pedir<{ publicada: number }>('POST', '/api/plataforma/ajustes', { version, motivo }),

  // -------------------------------------------------------------------------
  // Alertas de fallas graves
  // -------------------------------------------------------------------------
  // La CONFIGURACIÓN no viaja por aquí: `alertas` es un campo de los ajustes, así
  // que se guarda con guardarPlataforma o guardarInteligencia, y hereda, valida
  // y se versiona como todo lo demás. Un segundo camino para escribir lo mismo
  // se desincronizaría a la primera.
  //
  // Lo que sí viene de aquí es lo que aquellos no pueden dar: qué clases de
  // falla sabe detectar el servidor, qué se avisó de verdad, y la prueba.

  alertas: (tenant?: string) =>
    pedir<VistaAlertas>('GET', tenant
      ? `/api/tenants/${encodeURIComponent(tenant)}/alertas`
      : '/api/plataforma/alertas'),

  /** Manda un correo de prueba a los destinos QUE SE LE PASAN, no a los
   *  guardados: se usa mientras se escribe la lista, antes de guardarla.
   *
   *  Sin esto nadie sabe si la configuración está bien hasta el primer
   *  incidente, que es el peor momento para descubrir un typo en una dirección. */
  probarAlertas: (destinos: string[]) =>
    pedir<{ enviados: number; fallos: Record<string, string> }>(
      'POST', '/api/plataforma/alertas', { destinos }),

  // -------------------------------------------------------------------------
  // Credenciales de los proveedores de modelo
  // -------------------------------------------------------------------------
  // No hay método para leer una clave, y no es un olvido: el API no la devuelve
  // por ningún camino. Se registra y se reemplaza.

  credenciales: (tenant?: string) =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean; proveedores: ProveedorModelo[] }>(
      'GET', rutaCredenciales(tenant)),

  // Todas las de todos los clientes. Responde "¿quién factura contra su propio
  // contrato y quién contra el nuestro?" sin abrir treinta pantallas.
  // Los precios se administran aquí, no en el .env: un proveedor los cambia sin
  // avisarnos, y ajustarlos no puede exigir un reinicio.
  // Los proveedores de modelo. Son datos: agregar uno que hable un protocolo ya
  // implementado —Groq, DeepSeek, Azure, un vLLM propio— es una fila y una
  // credencial con su base_url, sin desplegar nada.
  proveedores: () =>
    pedir<{ proveedores: ProveedorModelo[]; implementaciones: Implementacion[] }>(
      'GET', '/api/plataforma/proveedores'),

  guardarProveedor: (p: ProveedorModelo) =>
    pedir<{ guardado: boolean }>('PUT', '/api/plataforma/proveedores', p),

  borrarProveedor: (id: string) =>
    pedir<{ borrado: boolean }>('DELETE', `/api/plataforma/proveedores/${encodeURIComponent(id)}`),

  precios: () =>
    pedir<{ precios: Precio[]; ayuda: string }>('GET', '/api/plataforma/precios'),

  guardarPrecio: (p: Precio) =>
    pedir<{ guardado: boolean }>('PUT', '/api/plataforma/precios', p),

  borrarPrecio: (proveedor: string, modelo: string) =>
    pedir<{ borrado: boolean }>('DELETE',
      `/api/plataforma/precios/${encodeURIComponent(proveedor)}/${encodeURIComponent(modelo)}`),

  // La puerta para pasar a modo agente. No prohíbe: informa.
  promocion: (id: string) =>
    pedir<{ veredicto: Veredicto; evidencia: Record<string, unknown> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/promocion`),

  // Lo que hace falta para conectar un agente, y qué vería si se conecta.
  mcp: (id: string) =>
    pedir<DescubrimientoMCP>('GET', `/api/tenants/${encodeURIComponent(id)}/mcp`),

  credencialesTodas: () =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean }>(
      'GET', '/api/plataforma/credenciales?todas=1'),

  guardarCredencial: (tenant: string | undefined, c: {
    proveedor: string; clave: string; base_url?: string; organizacion?: string; nota?: string
  }) =>
    pedir<{ guardada: boolean; cifrada: boolean; caracteres: number; recortados: number }>(
      'PUT', rutaCredenciales(tenant), c),

  activarCredencial: (tenant: string | undefined, proveedor: string, activa: boolean) =>
    pedir<{ activa: boolean }>(
      'POST', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`, { activa }),

  borrarCredencial: (tenant: string | undefined, proveedor: string) =>
    pedir<{ borrada: boolean }>(
      'DELETE', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`),
}

// Sin tenant son las de la plataforma: la cuenta contra la que factura todo el
// que no traiga la suya.
function rutaCredenciales(tenant?: string) {
  return tenant
    ? `/api/tenants/${encodeURIComponent(tenant)}/credenciales`
    : '/api/plataforma/credenciales'
}
